import { h, Component, createRef } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalSalePrescription from '../../components/dyrejournal/salePrescription';

const COLLAPSED_MAX_HEIGHT = 200;

const fieldSorter = (fields) => (a, b) => fields.map(o => {
    let dir = 1;
    if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
    }
    return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
}).reduce((p, n) => p ? p : n, 0);

@observer
class JournalListMini extends Component {
	constructor(props) {
        super(props);
        this.state = {
            selectedJournals: [],
            isOverflowing: false,
        };
        this.tableContainerRef = createRef();
        this.tableScrollerRef = createRef();
    }

    // The "Show more" / "Show less" buttons only switch the max-height of the
    // table container. Only offer them when the content is taller than the
    // collapsed height, otherwise clicking them does nothing visible.
    //
    // Measure the .table-responsive element, not the d-flex wrapper: Bootstrap
    // gives .table-responsive overflow-x: auto, which makes it a scroll
    // container. The single-line flex wrapper clamps its line to the max-height
    // and stretches the child to it, so the child scrolls and the wrapper's own
    // scrollHeight never exceeds the collapsed height (TICKET-01665).
    measureOverflow = () => {
        const el = this.tableScrollerRef.current;
        if (!el) {
            return;
        }
        const isOverflowing = el.scrollHeight > COLLAPSED_MAX_HEIGHT + 1;
        if (isOverflowing !== this.state.isOverflowing) {
            this.setState({ isOverflowing });
        }
    }

    onClickEditJournal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id, animal } = e.target.closest('.journalLine').dataset;
        const idInt = parseInt(id, 10);
        const animalInt = parseInt(animal, 10);

        const {
            drawerLevel = 1,
            saveSale = () => {},
            saleIsLocked = false,
            useBottomDrawer = false,
        } = this.props;
		const { appState, saleStore, journalStore } = this.props.stores;
        const { drawerHeightLarge } = appState;
        const { newSale } = saleStore;
        const { animals } = newSale;

        // Guard: only mutate newSale.journals when the journal actually belongs
        // to the current sale. Browsing historical journals (e.g. via
        // viewAnimalJournals) must not pollute the in-progress sale, which
        // would later cause a stray saveSale() to insert an unintended sale.
        const mergeJournalIntoSale = async () => {
            const { newJournal } = journalStore;
            const { newSale: currentSale } = saleStore;
            const { journals: currentJournals = [], id: currentSaleId } = currentSale;
            const idx = currentJournals.findIndex(a => a.id === newJournal.id);
            if (idx > -1) {
                saleStore.updateObjectKeyArrValue('newSale', 'journals', idx, newJournal);
                await saveSale({ calledFrom: 'listJournalMini.onClickEditJournal.callback', fieldsToSave: ['journals'] });
            } else if (currentSaleId && newJournal.sale === currentSaleId) {
                saleStore.updateObjectKeyValue('newSale', 'journals', [...currentJournals, newJournal]);
                await saveSale({ calledFrom: 'listJournalMini.onClickEditJournal.callback', fieldsToSave: ['journals'] });
            }
        };

        if (useBottomDrawer) {
            appState.openDrawer('editJournal', {
                height: drawerHeightLarge,
                isNew: false,
                hideAdmin: true,
                saveSale,
                saleIsLocked,
                id: idInt,
                animals: animals.filter(a => a.id === animalInt),
                callback: mergeJournalIntoSale,
            }, drawerLevel + 1);
        } else {
            appState.openDrawerRight('editJournal', {
                isNew: false,
                hideAdmin: true,
                saveSale,
                saleIsLocked,
                id: idInt,
                animals: animals.filter(a => a.id === animalInt),
                callback: mergeJournalIntoSale,
            }, drawerLevel + 1);
        }
    }

    onClickViewJournal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.journalLine').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;

        appState.openDrawer('viewJournal', {
            hideAdmin: true,
            height: drawerHeightLarge,
            id: parseInt(id, 10),
        }, drawerLevel + 1);
    }

    // selectJournal(j.id, e.currentTarget.checked, e)
    selectJournal = (id, checked, e) => {
        // e.stopPropagation();
        // e.preventDefault();
        const idInt = parseInt(id, 10);
        const { selectedJournals } = this.state;
        const idx = selectedJournals.indexOf(idInt);
        if (idx > -1) {
            selectedJournals.splice(idx, 1);
        } else {
            selectedJournals.push(idInt);
        }
        this.setState({
            selectedJournals,
        });
    }

    sendJournals = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { selectedJournals } = this.state;
        const {
            journals = [],
            animals = [],
        } = this.props;
        const journalsToSend = journals.filter(j => selectedJournals.indexOf(j.id) > -1);

        const { drawerLevel = 1 } = this.props;
        const { appState, animalStore } = this.props.stores;
        const { drawerHeightLarge } = appState;
        const { animal } = animalStore;

        appState.openDrawer('sendJournal', {
            hideAdmin: true,
            height: drawerHeightLarge,
            journals: journalsToSend,
            animals: [animal],
        }, drawerLevel + 2);
    }

    // onClickPrintPrescriptoin = e => {
    //     // console.log('onClickPrintPrescriptoin', e);
    //     // Render Prescription component with data
    //     const prescription = <DyrejournalSalePrescription stores={this.props.stores} />;
    //     // Append to document
    //     const printDiv = document.createElement('div');
    //     printDiv.className = 'prescription';
    //     document.body.appendChild(printDiv);
    //     render(prescription, printDiv);
    //     // Trigger print dialog
    //     window.print();
    //     // Remove the appended div
    //     document.body.removeChild(printDiv);
    // // Re-render the receipt component to reset it
    // render(null, printDiv);
    // }

    componentDidMount() {
        const { collapse = false } = this.props;
        this.setState({
            collapsed: collapse,
            selectedJournals: [],
        }, this.measureOverflow);
    }

    componentDidUpdate() {
        this.measureOverflow();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.journals !== this.props.journals) {
            this.setState({
                selectedJournals: [],
            });
        }
    }

    renderDiagnoses(journal) {
        const diagnoses = (journal.diagnoses || []).filter(d => d && (d.name || d.diagnoseCode));
        if (diagnoses.length === 0) {
            return null;
        }
        return diagnoses.map(d => (
            <span class='d-block' key={d.uuidv4 || `${d.diagnoseCode}-${d.name}`}>
                {d.name}
                {d.diagnoseCode && <small class='text-body-secondary ms-1'>({d.diagnoseCode})</small>}
            </span>
        ));
    }

    renderCompactStyle() {
        const { showAllJournals = false } = this.state;
        const {
            journals = [],
            classNames = '',
            viewOnly = false,
            darkmode = false,
            maxItems = 5,
        } = this.props;

        if (!journals || journals.length === 0) return null;

        const sortedJournals = journals.sort(fieldSorter(['-date']));
        const displayJournals = showAllJournals ? sortedJournals : sortedJournals.slice(0, maxItems);
        const remainingCount = journals.length - maxItems;
        const hasMore = remainingCount > 0;

        return (
            <div class={`compact-section ${darkmode ? 'darkmode' : ''} ${classNames}`} style='margin: 0 8px 8px 8px;'>
                <div class='compact-field-table'>
                    <div class='compact-field-row'>
                        <div class='compact-field-label-cell'>
                            <i class='fa-solid fa-clock-rotate-left me-1' style='font-size: 10px; opacity: 0.6;' />
                            Historikk
                        </div>
                        <div class='compact-field-input-cell'>
                            <span class='compact-field-value' style='display: flex; flex-direction: column; gap: 3px;'>
                                {displayJournals.map((j, idx) => {
                                    const lineSaleIsLocked = j.closed;
                                    const dateStr = util.formatDate(j.date, { locale: 'nb-NO' }).split(',')[0];
                                    const complaintShort = j.chiefComplaint ? j.chiefComplaint.substring(0, 40) : '';
                                    const complaintLong = j.chiefComplaint ? j.chiefComplaint.substring(0, 100) : '';
                                    const diagnoseNames = (j.diagnoses || []).map(d => d && d.name).filter(Boolean).join(', ');
                                    return (
                                        <span
                                            class='journalLine'
                                            onClick={(lineSaleIsLocked || viewOnly) ? this.onClickViewJournal : this.onClickEditJournal}
                                            data-id={j.id}
                                            data-animal={j.animal}
                                            style='cursor: pointer; display: flex; align-items: center; font-size: 11px;'
                                        >
                                            {(lineSaleIsLocked || viewOnly) ? <i class='fas fa-eye me-1' style='font-size: 9px; opacity: 0.5;'></i> : <i class='fas fa-edit me-1' style='font-size: 9px; opacity: 0.5;'></i>}
                                            <strong class='me-1'>{dateStr}</strong>
                                            {j.title && <span class='me-1'>{j.title}</span>}
                                            {complaintShort && <span class='text-muted d-inline d-md-none'>{complaintShort}{j.chiefComplaint.length > 40 ? '...' : ''}</span>}
                                            {complaintLong && <span class='text-muted d-none d-md-inline'>{complaintLong}{j.chiefComplaint.length > 100 ? '...' : ''}</span>}
                                            {diagnoseNames && <span class='ms-1 fst-italic'>{diagnoseNames}</span>}
                                        </span>
                                    );
                                })}
                                {hasMore && (
                                    <span
                                        class='text-primary'
                                        style='font-size: 10px; cursor: pointer;'
                                        onClick={() => this.setState({ showAllJournals: !showAllJournals })}
                                    >
                                        {showAllJournals ? (
                                            <><i class='fa-solid fa-chevron-up me-1' style='font-size: 8px;' />Vis mindre</>
                                        ) : (
                                            <>+{remainingCount} til</>
                                        )}
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { useCompactStyle = false } = this.props;

        if (useCompactStyle) {
            return this.renderCompactStyle();
        }

        const { selectedJournals, collapsed = false, isOverflowing = false } = this.state;
        const {
            animals = [],
            journals = [],
            classNames = '',
            showNewJournal = true,
            showNewBooking = true,
            journalsTitle,
            saleIsLocked,
            collapse = false,
            viewOnly = false,
            noSendTo = false,
            showHeader = true,
        } = this.props;
        const { userStore, animalStore } = this.props.stores;
        const { user } = userStore;
        const { owners } = animalStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        return (<>
            {journals && journals.length > 0 && <>
                {showHeader && <>
                    <div class={`mt-3 mx-4 font-weight-lighter px-3 py-1 text-muted`}>
                        <small class='text-uppercase'>
                            <Text id='dyrejournal.animals-journals'>Animal journals</Text>
                        </small>
                    </div>
                </>}

                <div class={`${darkmode ? 'bg-darkmode' : 'bg-lightmode'} d-flex flex-column py-0 px-0 ${classNames}`}>

                {/* <div class={`d-flex flex-column mx-3 px-2 py-3 mt-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}> */}

                {/* <div class='d-flex flex-column box-container mx-4 mt-2 bg-light p-3' style='border-radius: 1.0em !important;'> */}

                    <div ref={this.tableContainerRef} class='d-flex w-100 font-style-narrow' style={`${collapsed ? `max-height: ${COLLAPSED_MAX_HEIGHT}px;` : 'max-height: 60vh;'} overflow-y: auto;`}>
                        <div ref={this.tableScrollerRef} class='table-responsive w-100'>
                            <small>
                                <table class='table table-striped table-sm mb-0'>
                                    <thead class=''>
                                        <tr>
                                            <th scope='col'>&nbsp;</th>
                                            <th scope='col'><Text id='animal.journal-id'>Id</Text></th>
                                            <th scope='col'><Text id='animal.journal-date'>Journal date</Text></th>
                                            <th scope='col' class='text-center'><Text id='animal.journal-weight'>Weight</Text></th>
                                            <th scope='col'><Text id='animal.journal-chiefComplaint'>chiefComplaint</Text></th>
                                            <th scope='col'><Text id='animal.journal-treatmentPlan'>treatmentPlan</Text></th>
                                            <th scope='col'><Text id='animal.journal-diagnoses'>Diagnoses</Text></th>
                                            <th scope='col' class='w-10'>&nbsp;</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {journals.sort(fieldSorter(['-date'])).map(j => {
                                            const diff = util.dateDiff(j.date);
                                            const lineSaleIsLocked = j.closed;
                                            const isSelected = selectedJournals.indexOf(j.id) > -1;
                                            return (<>
                                                <tr
                                                    className={`journalLine`}
                                                    onClick={(lineSaleIsLocked || viewOnly) ? this.onClickViewJournal : this.onClickEditJournal}
                                                    data-id={j.id}
                                                    data-animal={j.animal}
                                                >
                                                    <td class={`${isSelected ? 'bg-success-subtle' : ''}`}>{(lineSaleIsLocked || viewOnly) ? <i class='fas fa-eye'></i> : <i class='fas fa-edit'></i>}</td>
                                                    <td class={`${isSelected ? 'bg-success-subtle' : ''}`}>{j.journalNumber || j.id}</td>
                                                    <td class={`${isSelected ? 'bg-success-subtle' : ''}`}>
                                                        {j.title ? <span class='font-weight-bold me-1'>{j.title}:</span> : ''}{util.formatDate(j.date, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}
                                                    </td>
                                                    <td class={`${isSelected ? 'bg-success-subtle' : ''} text-center`}>{j.weight > 0 ? `${j.weight}kg` : ''}</td>
                                                    <td class={`${isSelected ? 'bg-success-subtle' : ''}`}>
                                                        {j.chiefComplaint && <>
                                                            <Markdown markdown={j.chiefComplaint || ''} markedOpts={localUtil.getMarkdownOptions()} />
                                                        </>}
                                                    </td>
                                                    <td class={`${isSelected ? 'bg-success-subtle' : ''}`}>{j.treatmentPlan}</td>
                                                    <td class={`${isSelected ? 'bg-success-subtle' : ''}`}>{this.renderDiagnoses(j)}</td>
                                                    <td class={`w-10 ${isSelected ? 'bg-success-subtle' : ''}`}>
                                                        {noSendTo ? '' : <>
                                                            <div class="form-check form-switch float-end ps-0 my-0">
                                                                <input
                                                                class="form-check-input fs-2 my-0 ms-0"
                                                                type='checkbox'
                                                                data-id={j.id}
                                                                // checked={isSelected ? 'checked' : ''}
                                                                // onClick={this.selectJournal}
                                                                checked={!!isSelected}
                                                                value={1}
                                                                onChange={e => this.selectJournal(j.id, e.currentTarget.checked, e)}
                                                                onClick={e => e.stopPropagation()}         // ← avoid tr onClick conflicts
                                                            />
                                                            </div>



                                                        </>}
                                                    </td>
                                                </tr>
                                            </>)
                                        })}
                                    </tbody>
                                </table>
                            </small>
                        </div>
                    </div>
                    {collapsed ? <>
                        {isOverflowing && <div class='d-flex justify-content-center'>
                            <button type='button' class='btn btn-sm btn-link mt-0' onClick={() => this.setState({ collapsed: false })}>
                                <i class='fa-duotone fa-chevron-down me-2' />
                                <Text id='input.show-more'>Show more</Text>
                            </button>
                        </div>}
                    </> : <>
                        {isOverflowing && <div class='d-flex justify-content-center'>
                            <button type='button' class='btn btn-sm btn-link mt-0' onClick={() => this.setState({ collapsed: true })}>
                                <i class='fa-duotone fa-chevron-up me-2' />
                                <Text id='input.show-less'>Show less</Text>
                            </button>
                        </div>}
                    </>}

                    {selectedJournals.length > 0 && <>
                        <button type='button' class='btn btn-primary rounded-pill' onClick={this.sendJournals}>
                            <i class='fas fa-paper-plane'></i> <Text id='animal.send-journal'>Send journal</Text>
                        </button>
                    </>}

                </div>
            </>}
        </>);
    }
}

export default JournalListMini;
